import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ScoringEvidenceInlineQuote,
  ScoringEvidencePages,
  ScoringEvidenceQuoteChip,
  buildLocatedAnchors,
  findAnchorForQuote,
  prefetchNeighborPageImages,
  splitReasonByQuotes,
  stripQuoteBrackets,
  useScoringEvidenceAnchors,
} from './ScoringEvidenceOverlay';
import useAppScrollLock from '../../hooks/useAppScrollLock';
import Dialog from '../../components/common/Dialog';
import { getScoringStudentName } from './scoringResultMeta';
import {
  collectEvidenceQuotes,
  formatSourcePages,
  getSourcePageNumbers,
  isSplitFromSharedFile,
  shouldShowOwnAnswer,
  splitAnswerByQuotes,
} from './scoringSubmissionSource';
import { getGradeOnlySummary, isGradeOnlyResult } from './scoringGradeMode';
import { REVIEW_QUEUE_VISIBLE_LIMIT, buildReviewQueue } from './scoringReviewQueue';
import { isPlaceholderStudentName } from '../../utils/studentNamePolicy';
import { trackScoringUxEvent } from '../../utils/eventTracker';
import {
  buildFeedbackEdit,
  buildItemScoreEdit,
  buildStudentNameEdit,
  isTeacherEditedReason,
} from './scoringResultEdits';

const getResultTotalScore = (result) => {
  if (Array.isArray(result?.question_results) && result.question_results.length > 0) {
    return result.question_results.reduce((sum, item) => sum + (Number(item?.score) || 0), 0);
  }
  if (Array.isArray(result?.item_scores) && result.item_scores.length > 0) {
    return result.item_scores.reduce((sum, item) => sum + (Number(item?.score) || 0), 0);
  }
  return result?.total_score ?? result?.score ?? 0;
};

const getRubricItemName = (item) => (
  item?.item_name ||
  item?.name ||
  item?.title ||
  item?.criteria ||
  item?.evaluation_item ||
  item?.category ||
  '평가요소'
);

const getRubricItemMaxScore = (item) => {
  const candidates = [
    item?.max_score,
    item?.max_points,
    item?.points,
    item?.point,
    item?.score,
  ];
  const value = candidates.find(v => v !== undefined && v !== null && v !== '');
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const getRubricTotalPoints = (rubric) => {
  const explicitTotal = Number(rubric?.total_points || rubric?.total_score || rubric?.max_score || 0);
  if (Number.isFinite(explicitTotal) && explicitTotal > 0) return explicitTotal;
  const items = Array.isArray(rubric?.evaluation_items) ? rubric.evaluation_items : [];
  return items.reduce((sum, item) => sum + getRubricItemMaxScore(item), 0);
};

const getQuestionTitle = (question, index) => (
  question?.title ||
  question?.question_title ||
  question?.name ||
  question?.subject ||
  `${question?.question_id || index + 1}번 문항`
);

const getQuestionMaxScore = (question) => {
  const numeric = Number(question?.max_points || question?.max_score || question?.points || 0);
  return Number.isFinite(numeric) ? numeric : 0;
};

const findRubricItem = (rubric, itemName) => (
  rubric?.evaluation_items?.find(item => getRubricItemName(item) === itemName)
);

const getMatchedCriteria = (rubric, item) => {
  const rubricItem = findRubricItem(rubric, item?.item_name);
  return rubricItem?.scoring_levels?.find(level => level.level === item?.matched_level)?.criteria;
};

const normalizeCriterionChecks = (item) => {
  const raw = item?.criterion_checks || item?.evidence_checks || item?.checks || [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((check) => {
      if (!check) return null;
      if (typeof check === 'string') {
        const text = check.trim();
        return text ? { label: text, status: 'met', evidence: '', explanation: '' } : null;
      }
      const label = String(check.label || check.criterion || check.name || check.condition || '판정 기준').trim();
      const status = ['met', 'missing', 'partial', 'needs_review'].includes(check.status)
        ? check.status
        : (check.met === false ? 'missing' : 'met');
      return {
        label,
        status,
        evidence: String(check.evidence || check.quote || check.answer_quote || '').trim(),
        explanation: String(check.explanation || check.reason || check.note || '').trim(),
      };
    })
    .filter(Boolean)
    .slice(0, 6);
};

const criterionStatusLabel = {
  met: '충족',
  missing: '부족',
  partial: '일부',
  needs_review: '확인',
};

const getItemScoreLabel = (item, gradeOnly = false) => {
  if (gradeOnly || isGradeOnlyResult(item)) {
    return item?.matched_level || getGradeOnlySummary(item, '등급');
  }
  return `${item?.score || 0}/${item?.max_score || 0}점`;
};

// evidence(근거 위치) 연동 정보. 없으면 기존 표시와 동일하게 동작한다.
const EMPTY_EVIDENCE_LINK = null;

const isEnterKey = (event) => event.key === 'Enter' && !event.nativeEvent?.isComposing && event.keyCode !== 229;

// Dialog 는 document 에서 Esc 로 닫히므로 편집 중 Esc 는 여기서 멈춘다.
const handleEditorKeyDown = (event, { onSave, onCancel, saveOnEnter = true }) => {
  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    onCancel();
    return;
  }
  if (isEnterKey(event) && (saveOnEnter || event.metaKey || event.ctrlKey)) {
    event.preventDefault();
    onSave();
  }
};

function InlineEditActions({ saving, onSave, onCancel }) {
  return (
    <div className="scoring-review-edit-actions">
      <button type="button" className="scoring-review-edit-cancel" onClick={onCancel} disabled={saving}>
        취소
      </button>
      <button type="button" className="scoring-review-edit-save" onClick={onSave} disabled={saving}>
        {saving ? '저장 중' : '저장'}
      </button>
    </div>
  );
}

function InlineEditError({ message }) {
  if (!message) return null;
  return <p className="scoring-review-edit-error" role="alert">{message}</p>;
}

function ScoringScoreEditor({ label, maxScore, draft, saving, error, onChange, onSave, onCancel }) {
  const keyHandlers = { onSave, onCancel };
  return (
    <div className="scoring-review-item-editor">
      <label className="scoring-review-score-input-row">
        <span>점수</span>
        <input
          aria-label={`${label} 점수`}
          autoFocus
          disabled={saving}
          inputMode="decimal"
          onChange={(event) => onChange({ score: event.target.value })}
          onKeyDown={(event) => handleEditorKeyDown(event, keyHandlers)}
          type="text"
          value={draft.score}
        />
        {Number(maxScore) > 0 && <em>/ {maxScore}점</em>}
      </label>
      <label className="scoring-review-reason-input">
        <span>판단 근거</span>
        <textarea
          aria-label={`${label} 판단 근거`}
          disabled={saving}
          onChange={(event) => onChange({ reason: event.target.value })}
          onKeyDown={(event) => handleEditorKeyDown(event, { ...keyHandlers, saveOnEnter: false })}
          rows={3}
          value={draft.reason}
        />
      </label>
      <InlineEditError message={error} />
      <InlineEditActions saving={saving} onSave={onSave} onCancel={onCancel} />
    </div>
  );
}

export function ScoringCriterionChecks({ gradeOnly = false, item, itemIndex = null, questionIndex = null, evidenceLink = EMPTY_EVIDENCE_LINK }) {
  const checks = normalizeCriterionChecks(item);
  const capReason = String(item?.score_cap_reason || '').trim();
  const reviewReason = String(item?.review_reason || '').trim();
  if (checks.length === 0 && !capReason && !reviewReason) return null;
  const metCount = checks.filter(check => check.status === 'met').length;
  const issueCount = checks.filter(check => check.status !== 'met').length + (capReason ? 1 : 0);
  // "충족 2 · 점검 1" 처럼 상태 요약. 전부 충족이면 "충족 N".
  const summaryLabel = issueCount > 0
    ? [metCount > 0 ? `충족 ${metCount}` : '', `점검 ${issueCount}`].filter(Boolean).join(' · ')
    : `충족 ${checks.length || 1}`;
  const hasLocatedCheck = Boolean(evidenceLink) && checks.some((check, index) => (
    check.evidence && findAnchorForQuote(evidenceLink.located, {
      source: 'criterion_check', itemIndex, checkIndex: index, questionIndex, quote: check.evidence,
    })
  ));

  return (
    <details className="scoring-criterion-checks" open={hasLocatedCheck || undefined}>
      <summary>
        <span>기준 조건별 확인</span>
        <em>{summaryLabel}</em>
      </summary>
      <div className="scoring-criterion-check-list">
        {checks.map((check, index) => {
          const anchor = evidenceLink && check.evidence
            ? findAnchorForQuote(evidenceLink.located, {
              source: 'criterion_check', itemIndex, checkIndex: index, questionIndex, quote: check.evidence,
            })
            : null;
          return (
            <div key={`${check.label}-${index}`} className={`scoring-criterion-check is-${check.status}`}>
              <div className="scoring-criterion-check-head">
                <strong>{check.label}</strong>
                <span>{criterionStatusLabel[check.status] || '확인'}</span>
              </div>
              {check.evidence && (
                <ScoringEvidenceQuoteChip
                  anchor={anchor}
                  activeKey={evidenceLink?.activeKey}
                  onSelect={evidenceLink?.onSelectFromChip}
                  registerChipRef={evidenceLink?.registerChipRef}
                  className="scoring-criterion-evidence"
                >
                  {anchor ? stripQuoteBrackets(check.evidence) : check.evidence}
                </ScoringEvidenceQuoteChip>
              )}
              {check.explanation && <p>{check.explanation}</p>}
            </div>
          );
        })}
        {capReason && (
          <div className="scoring-criterion-check is-needs_review">
            <div className="scoring-criterion-check-head">
              <strong>{gradeOnly ? '수행수준 확인 사유' : '점수 조정 사유'}</strong>
              <span>확인</span>
            </div>
            <p>{capReason}</p>
          </div>
        )}
        {reviewReason && (
          <div className="scoring-criterion-check is-needs_review">
            <div className="scoring-criterion-check-head">
              <strong>근거 확인 필요</strong>
              <span>확인</span>
            </div>
            <p>{reviewReason}</p>
          </div>
        )}
      </div>
    </details>
  );
}

function ScoringEvidenceItem({
  criteria = '',
  gradeOnly = false,
  item,
  itemIndex = null,
  questionIndex = null,
  evidenceLink = EMPTY_EVIDENCE_LINK,
  edit = null,
}) {
  const scoreLabel = getItemScoreLabel(item, gradeOnly);
  // 기준 조건별 확인에서 이미 칩으로 보여주는 anchor. 항목 근거 칩이 같은 anchor를 가리키면 중복이라 숨긴다.
  // (프로덕션 실측: 겹침·중복의 대부분이 "같은 구절을 항목 근거와 기준 조건 근거로 두 번 인용"한 경우)
  const checkAnchorKeys = useMemo(() => {
    if (!evidenceLink) return new Set();
    const keys = new Set();
    normalizeCriterionChecks(item).forEach((check, index) => {
      if (!check.evidence) return;
      const anchor = findAnchorForQuote(evidenceLink.located, {
        source: 'criterion_check', itemIndex, checkIndex: index, questionIndex, quote: check.evidence,
      });
      if (anchor) keys.add(anchor.quote_key);
    });
    return keys;
  }, [evidenceLink, item, itemIndex, questionIndex]);

  // AI 근거 본문 안의 「…」 인용이 anchor와 연결되면 본문에서 바로 클릭할 수 있게 한다(별도 칩 반복 축소).
  const reasonParts = useMemo(() => (
    splitReasonByQuotes(item.reason, (quote) => (evidenceLink
      ? findAnchorForQuote(evidenceLink.located, { source: 'evidence_quote', itemIndex, questionIndex, quote })
      : null))
  ), [item.reason, evidenceLink, itemIndex, questionIndex]);
  const inlineAnchorKeys = useMemo(() => new Set(reasonParts.filter((p) => p.anchor).map((p) => p.anchor.quote_key)), [reasonParts]);

  // 위치가 확인된 evidence_quotes만 칩으로 보여준다(미확인 인용문은 기존처럼 노출하지 않음).
  // 같은 anchor는 한 번만, 기준 조건 칩이나 본문 인라인 링크로 이미 보이는 anchor는 생략한다.
  const locatedQuotes = useMemo(() => {
    if (!evidenceLink || !Array.isArray(item.evidence_quotes)) return [];
    const seen = new Set();
    const out = [];
    item.evidence_quotes.forEach((quote) => {
      const anchor = findAnchorForQuote(evidenceLink.located, { source: 'evidence_quote', itemIndex, questionIndex, quote });
      if (!anchor || seen.has(anchor.quote_key)) return;
      seen.add(anchor.quote_key);
      if (checkAnchorKeys.has(anchor.quote_key) || inlineAnchorKeys.has(anchor.quote_key)) return;
      out.push({ quote, anchor });
    });
    return out;
  }, [evidenceLink, item.evidence_quotes, itemIndex, questionIndex, checkAnchorKeys, inlineAnchorKeys]);

  const itemLabel = item.item_name || '평가요소';
  if (edit?.isEditing) {
    return (
      <div className="scoring-review-item-result is-editing">
        <div className="scoring-review-row-head">
          <strong>{itemLabel}</strong>
        </div>
        <ScoringScoreEditor label={itemLabel} maxScore={item.max_score} {...edit.editorProps} />
      </div>
    );
  }

  return (
    <div className="scoring-review-item-result">
      <div className="scoring-review-row-head">
        <strong>{itemLabel}</strong>
        <span className="scoring-review-row-score">
          {scoreLabel}
          {edit?.onStart && (
            <button
              type="button"
              className="scoring-review-inline-edit-btn"
              onClick={edit.onStart}
              aria-label={`${itemLabel} 점수 고치기`}
            >
              고치기
            </button>
          )}
        </span>
      </div>
      {item.teacher_score ? (
        <p className="scoring-review-criteria-caption">
          <b>선생님 점수</b>
          {Number.isFinite(Number(item.ai_score)) && item.ai_score !== null ? `AI는 ${item.ai_score}점을 줬어요` : '직접 고친 점수예요'}
        </p>
      ) : (criteria || item.matched_level) && (
        <p className="scoring-review-criteria-caption">
          <b>기준</b>{criteria || item.matched_level}
        </p>
      )}
      {item.reason && (
        <p className="scoring-review-ai-reason">
          <b>{isTeacherEditedReason(item) ? '선생님이 고친 근거' : 'AI 근거'}</b>
          {reasonParts.map((part, index) => (part.anchor ? (
            <ScoringEvidenceInlineQuote
              key={`${part.anchor.quote_key}-${index}`}
              anchor={part.anchor}
              activeKey={evidenceLink?.activeKey}
              onSelect={evidenceLink?.onSelectFromChip}
              registerChipRef={evidenceLink?.registerChipRef}
            >
              {part.text}
            </ScoringEvidenceInlineQuote>
          ) : (
            <React.Fragment key={index}>{part.text}</React.Fragment>
          )))}
        </p>
      )}
      {locatedQuotes.length > 0 && (
        <div className="scoring-evidence-quote-list">
          {locatedQuotes.map(({ quote, anchor }) => (
            <ScoringEvidenceQuoteChip
              key={anchor.quote_key}
              anchor={anchor}
              activeKey={evidenceLink.activeKey}
              onSelect={evidenceLink.onSelectFromChip}
              registerChipRef={evidenceLink.registerChipRef}
              className="scoring-evidence-quote"
            >
              {stripQuoteBrackets(quote)}
            </ScoringEvidenceQuoteChip>
          ))}
        </div>
      )}
      <ScoringCriterionChecks
        gradeOnly={gradeOnly || isGradeOnlyResult(item)}
        item={item}
        itemIndex={itemIndex}
        questionIndex={questionIndex}
        evidenceLink={evidenceLink}
      />
    </div>
  );
}

export function ScoringDistributionNotice({ result }) {
  const adjustment = result?.distribution_adjustment;
  if (!adjustment?.applied) return null;
  return (
    <div className="scoring-distribution-notice">
      <strong>분포 요청을 반영했어요</strong>
      <p>
        {adjustment.previous_total_score}점에서 {adjustment.adjusted_total_score}점으로 다시 봤어요.
        기준표 안에서 비슷한 수준의 경계선 답안을 요청한 상·중·하 비율에 맞춰 조정한 결과예요.
      </p>
    </div>
  );
}

const buildSubmissionFallbackParts = (result) => {
  const directText = String(result?.answer_text || result?.text_content || result?.answer_excerpt || '').trim();
  const evidenceParts = [];

  if (Array.isArray(result?.question_results)) {
    result.question_results.forEach((question) => {
      if (question?.answer_excerpt) {
        evidenceParts.push(`[${question.question_title || question.question_id || '문항'}]\n${question.answer_excerpt}`);
      }
    });
  }

  if (Array.isArray(result?.item_scores)) {
    const reasons = result.item_scores
      .filter(item => item?.reason)
      .map(item => `- ${item.item_name || '평가요소'}: ${item.reason}`);
    if (reasons.length > 0) {
      evidenceParts.push(reasons.join('\n'));
    }
  }

  if (result?.detailed_feedback) {
    evidenceParts.push(`[종합 의견]\n${result.detailed_feedback}`);
  }

  return {
    originalText: directText,
    evidenceText: evidenceParts.join('\n\n').trim(),
  };
};

const isImagePreview = (fileType = '', filename = '') => {
  const lower = String(filename || '').toLowerCase();
  return String(fileType || '').startsWith('image/') || /\.(png|jpe?g|webp|gif|heic|heif)$/i.test(lower);
};

const isPdfPreview = (fileType = '', filename = '') => (
  String(fileType || '') === 'application/pdf' || String(filename || '').toLowerCase().endsWith('.pdf')
);

const getResultAttachments = (result) => {
  const rawAttachments = Array.isArray(result?.attachments) ? result.attachments : [];
  const normalized = rawAttachments
    .filter(item => item && (item.storage_path || item.answer_text || item.text_content))
    .map((item, index) => ({
      ...item,
      fileName: item.fileName || item.filename || `파일 ${index + 1}`,
      filename: item.filename || item.fileName || `파일 ${index + 1}`,
    }));
  if (normalized.length > 0) return normalized;
  if (result?.storage_path) {
    return [{
      fileName: result.fileName || result.filename || '제출물',
      filename: result.filename || result.fileName || '제출물',
      storage_path: result.storage_path,
      file_type: result.file_type,
      pages: result.pages,
    }];
  }
  return [];
};

const isTypingTarget = (target) => {
  if (!target) return false;
  const tag = String(target.tagName || '').toUpperCase();
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || Boolean(target.isContentEditable);
};

const buildPdfPreviewSrc = (url, page) => {
  const pageNumber = Number.parseInt(page, 10);
  const pageParam = Number.isFinite(pageNumber) && pageNumber > 0 ? `page=${pageNumber}&` : '';
  return `${url}#${pageParam}toolbar=0&navpanes=0&scrollbars=1&statusbar=0&messages=0&pagemode=none&view=FitH`;
};

function ScoringReviewWorkspace({
  actionSlot = null,
  className = '',
  getSubmissionLabel,
  initialSelectedIndex = 0,
  loadSubmissionPreview,
  onClose = null,
  questionSet = null,
  recordId = null,
  results = [],
  rubric = null,
  rubricText = '',
  title = '채점 결과를 검토하세요',
  variant = 'inline',
  onSaveResult = null,
}) {
  // 결과가 없어 모달을 렌더하지 않을 때는 배경 스크롤을 잠그지 않는다
  useAppScrollLock(variant === 'modal' && Array.isArray(results) && results.length > 0);

  const validResults = useMemo(() => (
    Array.isArray(results) ? results : []
  ), [results]);

  const firstNormalIndex = useMemo(() => {
    const index = validResults.findIndex(result => !result?.error);
    return index >= 0 ? index : 0;
  }, [validResults]);

  const resolvedInitialIndex = validResults[initialSelectedIndex]?.error
    ? firstNormalIndex
    : Math.min(Math.max(Number(initialSelectedIndex) || 0, 0), Math.max(validResults.length - 1, 0));
  const [selectedIndex, setSelectedIndex] = useState(resolvedInitialIndex);
  const [mobileTab, setMobileTab] = useState('submission');
  const [rubricCollapsed, setRubricCollapsed] = useState(() => variant === 'modal');
  const [selectedAttachmentIndex, setSelectedAttachmentIndex] = useState(0);
  const studentRailRef = useRef(null);
  const workspaceRef = useRef(null);
  const [submissionPreview, setSubmissionPreview] = useState({ loading: false, data: null, error: '' });
  const findNeighborIndex = useCallback((from, direction) => {
    for (let i = from + direction; i >= 0 && i < validResults.length; i += direction) {
      if (!validResults[i]?.error) return i;
    }
    return -1;
  }, [validResults]);
  // 검토창 사용 계측: 열 때 1번, 닫을 때 요약 1번만 보낸다(학생 이동마다 보내지 않음). 원문·이름은 보내지 않는다.
  const reviewUxRef = useRef({
    openedAt: Date.now(),
    nav: { button: 0, key: 0, card: 0 },
    modeByIndex: new Map(),
    originalToggles: 0,
    studentCount: 0,
    hasRecord: false,
  });
  reviewUxRef.current.studentCount = validResults.length;
  reviewUxRef.current.hasRecord = Boolean(recordId);
  const hasReviewResults = validResults.length > 0;
  useEffect(() => {
    const stats = reviewUxRef.current;
    if (!hasReviewResults) return undefined;
    stats.openedAt = Date.now();
    stats.nav = { button: 0, key: 0, card: 0 };
    stats.modeByIndex = new Map();
    stats.originalToggles = 0;
    trackScoringUxEvent('review_open', { studentCount: stats.studentCount, hasRecord: stats.hasRecord });
    return () => {
      const modes = Array.from(stats.modeByIndex.values());
      const countMode = mode => modes.filter(value => value === mode).length;
      trackScoringUxEvent('review_close', {
        studentCount: stats.studentCount,
        hasRecord: stats.hasRecord,
        elapsedMs: Date.now() - stats.openedAt,
        viewedCount: stats.modeByIndex.size,
        navButtonCount: stats.nav.button,
        navKeyCount: stats.nav.key,
        navCardCount: stats.nav.card,
        ownAnswerCount: countMode('own_answer'),
        overlayCount: countMode('overlay'),
        filePageCount: countMode('file_page'),
        fileCount: countMode('file'),
        originalToggleCount: stats.originalToggles,
      });
    };
  }, [hasReviewResults]);
  const selectStudent = useCallback((index, method) => {
    if (index < 0 || index === selectedIndex) return;
    reviewUxRef.current.nav[method] = (reviewUxRef.current.nav[method] || 0) + 1;
    setSelectedIndex(index);
  }, [selectedIndex]);
  const prevStudentIndex = findNeighborIndex(selectedIndex, -1);
  const nextStudentIndex = findNeighborIndex(selectedIndex, 1);
  const goToNeighborStudent = useCallback((direction, method = 'button') => {
    selectStudent(findNeighborIndex(selectedIndex, direction), method);
  }, [findNeighborIndex, selectStudent, selectedIndex]);
  const selectableStudentIndexes = useMemo(() => (
    validResults.map((result, index) => (result?.error ? -1 : index)).filter(index => index >= 0)
  ), [validResults]);
  const selectedPosition = selectableStudentIndexes.indexOf(selectedIndex) + 1;

  // 선택한 학생 카드가 레일 밖에 있으면 가운데로 가져온다(레일만 가로로 스크롤, 모달은 그대로).
  useEffect(() => {
    const rail = studentRailRef.current;
    const card = rail?.querySelector?.(`[data-student-index="${selectedIndex}"]`);
    if (!rail || !card || typeof card.getBoundingClientRect !== 'function') return;
    const railRect = rail.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    if (cardRect.left >= railRect.left && cardRect.right <= railRect.right) return;
    const left = rail.scrollLeft + (cardRect.left - railRect.left) - (railRect.width - cardRect.width) / 2;
    rail.scrollTo?.({ left: Math.max(0, left), behavior: 'smooth' });
  }, [selectedIndex, validResults.length]);

  // ←/→ 로 이전·다음 학생. 글을 고치는 중(입력칸)이거나 조합 키를 누른 때는 건드리지 않는다.
  useEffect(() => {
    if (validResults.length < 2) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (isTypingTarget(event.target)) return;
      if (variant !== 'modal' && !workspaceRef.current?.contains(event.target)) return;
      event.preventDefault();
      goToNeighborStudent(event.key === 'ArrowLeft' ? -1 : 1, 'key');
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [goToNeighborStudent, validResults.length, variant]);
  const [editing, setEditing] = useState(null);
  const selectedResult = validResults[selectedIndex] || validResults[firstNormalIndex] || null;
  useEffect(() => {
    setEditing(null);
  }, [selectedIndex]);
  const attachments = useMemo(() => getResultAttachments(selectedResult), [selectedResult]);
  const selectedAttachment = attachments[selectedAttachmentIndex] || attachments[0] || null;
  const previewTarget = useMemo(() => (
    selectedAttachment
      ? {
        ...selectedResult,
        ...selectedAttachment,
        answer_text: selectedResult?.answer_text || selectedAttachment.answer_text || '',
      }
      : selectedResult
  ), [selectedAttachment, selectedResult]);
  // 학생별 쪽 정보는 results[i].storage_path(분리 전 원본) 기준이라 다른 첨부를 보고 있을 때는 쓰지 않는다.
  const previewingSourceFile = !selectedAttachment?.storage_path
    || selectedAttachment.storage_path === selectedResult?.storage_path;
  const sourcePageNumbers = useMemo(() => (
    previewingSourceFile ? getSourcePageNumbers(selectedResult) : []
  ), [previewingSourceFile, selectedResult]);
  const ownAnswerPreferred = useMemo(() => (
    previewingSourceFile && shouldShowOwnAnswer(selectedResult, validResults)
  ), [previewingSourceFile, selectedResult, validResults]);
  const ownAnswerText = String(previewTarget?.answer_text || previewTarget?.text_content || '').trim();
  const [showFullOriginal, setShowFullOriginal] = useState(false);
  useEffect(() => {
    setShowFullOriginal(false);
  }, [selectedIndex]);
  // 답안 글이 비어 있어도 원본을 자동으로 열지 않는다(열면 다른 학생의 1쪽이 보인다). 원본은 버튼으로만 연다.
  const ownAnswerMode = ownAnswerPreferred && !showFullOriginal;
  const ownAnswerSegments = useMemo(() => (
    ownAnswerMode ? splitAnswerByQuotes(ownAnswerText, collectEvidenceQuotes(selectedResult)) : []
  ), [ownAnswerMode, ownAnswerText, selectedResult]);
  const selectedGradeOnly = isGradeOnlyResult(selectedResult, rubric);
  const selectedScoreLabel = selectedGradeOnly
    ? getGradeOnlySummary(selectedResult)
    : `${getResultTotalScore(selectedResult)}점`;
  const totalPoints = getRubricTotalPoints(rubric);
  const rubricItems = Array.isArray(rubric?.evaluation_items) ? rubric.evaluation_items : [];
  const questions = Array.isArray(questionSet?.questions) ? questionSet.questions : [];

  // 근거 위치(anchor): 기록이 저장된 경우에만 존재. 없으면 아래 로직은 전부 no-op.
  // 인용문·판정이 바뀌면(AI 수정, 점수 수정, 재채점 반영) anchor를 다시 받아 색·위치를 갱신한다.
  const evidenceRefreshKey = useMemo(() => JSON.stringify(validResults.map((r) => [r?.item_scores, r?.question_results])), [validResults]);
  const evidenceAnchors = useScoringEvidenceAnchors(recordId, { expectedCount: validResults.length, refreshKey: evidenceRefreshKey });
  const evidenceDoc = evidenceAnchors.byIndex?.[String(selectedIndex)] || null;
  const locatedAnchors = useMemo(() => buildLocatedAnchors(evidenceDoc), [evidenceDoc]);
  const [activeEvidenceKey, setActiveEvidenceKey] = useState(null);
  const submissionPanelRef = useRef(null);
  const feedbackPanelRef = useRef(null);
  // 학생을 바꾸면 이전 학생에서 내려간 스크롤(2쪽 등)이 남지 않게 두 패널을 맨 위로 되돌린다.
  useEffect(() => {
    submissionPanelRef.current?.scrollTo?.({ top: 0 });
    feedbackPanelRef.current?.scrollTo?.({ top: 0 });
  }, [selectedIndex]);
  const [evidenceOverlayUnavailable, setEvidenceOverlayUnavailable] = useState(false);
  const segmentRefs = useRef(new Map());
  const chipRefs = useRef(new Map());
  const registerSegmentRef = useCallback((key, node) => {
    if (node) segmentRefs.current.set(key, node);
    else segmentRefs.current.delete(key);
  }, []);
  const registerChipRef = useCallback((key, node) => {
    if (node) chipRefs.current.set(key, node);
    else chipRefs.current.delete(key);
  }, []);
  const isMobileLayout = () => (typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(max-width: 1180px)').matches
    : false);
  const scrollNodeIntoView = (node) => {
    if (!node || typeof node.scrollIntoView !== 'function') return;
    node.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  };
  // 하이라이트 클릭 → 오른쪽 인용문 칩으로
  const handleSelectFromSegment = useCallback((key) => {
    setActiveEvidenceKey(key);
    if (isMobileLayout()) setMobileTab('feedback');
    window.setTimeout(() => scrollNodeIntoView(chipRefs.current.get(key)), isMobileLayout() ? 80 : 0);
  }, []);
  // 인용문 칩 클릭 → 제출물 위 하이라이트로
  const handleSelectFromChip = useCallback((key) => {
    setActiveEvidenceKey(key);
    if (isMobileLayout()) setMobileTab('submission');
    window.setTimeout(() => scrollNodeIntoView(segmentRefs.current.get(key)), isMobileLayout() ? 80 : 0);
  }, []);
  const evidenceLink = useMemo(() => (
    locatedAnchors.length > 0
      ? {
        located: locatedAnchors,
        activeKey: activeEvidenceKey,
        onSelectFromChip: handleSelectFromChip,
        registerChipRef,
      }
      : null
  ), [locatedAnchors, activeEvidenceKey, handleSelectFromChip, registerChipRef]);

  useEffect(() => {
    setActiveEvidenceKey(null);
    setEvidenceOverlayUnavailable(false);
  }, [selectedIndex, recordId]);

  // 근거 위치가 준비된 파일은 페이지 이미지 + 판정 색 하이라이트로 보여준다(항상 표시).
  // - 계산이 끝났으면 위치 확정 anchor가 1개 이상일 때
  // - 아직 계산 중이어도 선업로드 이미지(image_url)가 있으면 먼저 이미지를 보여주고 매칭은 나중에 얹는다
  // anchor는 results[i].storage_path 기준이라 다른 첨부를 보고 있을 때는 적용하지 않는다.
  // 학생 답안 글을 먼저 보여 주는 경우(쪽 정보 없는 공유 PDF 등)는 예전 계산이 앞쪽(다른 학생 답안)에 근거를 찍어 두었을 수 있어 쓰지 않는다.
  const evidenceOverlayActive = useMemo(() => {
    if (!recordId || evidenceOverlayUnavailable || ownAnswerPreferred) return false;
    const pages = Array.isArray(evidenceDoc?.pages) ? evidenceDoc.pages : [];
    if (pages.length === 0) return false;
    if (selectedAttachment?.storage_path && selectedAttachment.storage_path !== selectedResult?.storage_path) return false;
    if (locatedAnchors.length > 0) return true;
    return evidenceDoc?.status === 'processing' && pages.every((p) => Boolean(p.image_url));
  }, [recordId, evidenceOverlayUnavailable, ownAnswerPreferred, evidenceDoc, selectedAttachment, selectedResult, locatedAnchors]);
  // anchor 응답을 아직 못 받았으면 기존 미리보기(원본 파일 다운로드)를 잠시 보류한다 → 같은 파일을 두 번 받지 않는다.
  const evidenceAnchorsPending = Boolean(recordId) && !ownAnswerPreferred && ['idle', 'loading'].includes(evidenceAnchors.status);
  let submissionMode = 'file';
  if (ownAnswerMode) submissionMode = 'own_answer';
  else if (evidenceOverlayActive) submissionMode = 'overlay';
  else if (sourcePageNumbers.length > 0) submissionMode = 'file_page';
  useEffect(() => {
    if (selectedResult) reviewUxRef.current.modeByIndex.set(selectedIndex, submissionMode);
  }, [selectedIndex, selectedResult, submissionMode]);
  // 현재 학생 이미지가 뜬 뒤 이웃 학생의 첫 페이지를 미리 받아 둔다(학생 전환 즉시 표시).
  useEffect(() => {
    if (!evidenceOverlayActive) return undefined;
    const timer = window.setTimeout(() => {
      prefetchNeighborPageImages(evidenceAnchors.byIndex, selectedIndex, validResults.length);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [evidenceOverlayActive, evidenceAnchors.byIndex, selectedIndex, validResults.length]);

  useEffect(() => {
    setSelectedIndex(resolvedInitialIndex);
  }, [resolvedInitialIndex, validResults.length]);

  useEffect(() => {
    setRubricCollapsed(variant === 'modal');
  }, [variant]);

  // 모달 변형의 ESC·스크림·닫기 버튼·포커스 트랩은 Dialog 프리미티브가 맡는다.

  useEffect(() => {
    setSelectedAttachmentIndex(0);
  }, [selectedIndex]);

  useEffect(() => {
    let cancelled = false;
    const fullText = String(previewTarget?.answer_text || previewTarget?.text_content || '').trim();

    if (!previewTarget) return undefined;

    // 하이라이트 오버레이가 그 파일을 보여주는 동안(또는 anchor 응답 대기 중), 학생 답안 글을 보여주는 동안에는
    // 원본 미리보기를 받지 않는다.
    if (evidenceOverlayActive || evidenceAnchorsPending || ownAnswerMode) {
      setSubmissionPreview((prev) => (prev.loading ? { loading: false, data: null, error: '' } : prev));
      return undefined;
    }

    if (typeof loadSubmissionPreview !== 'function') {
      setSubmissionPreview({
        loading: false,
        data: fullText
          ? {
              textContent: fullText,
              filename: previewTarget.fileName || previewTarget.filename || '학생 답안',
              fileType: 'text/plain',
            }
          : null,
        error: '',
      });
      return undefined;
    }

    setSubmissionPreview({ loading: true, data: null, error: '' });
    Promise.resolve(loadSubmissionPreview(previewTarget, selectedIndex))
      .then((data) => {
        if (cancelled) return;
        setSubmissionPreview({
          loading: false,
          data: data || (fullText
            ? {
                textContent: fullText,
                filename: previewTarget.fileName || previewTarget.filename || '학생 답안',
                fileType: 'text/plain',
              }
            : null),
          error: '',
        });
      })
      .catch(() => {
        if (cancelled) return;
        setSubmissionPreview({
          loading: false,
          data: fullText
            ? {
                textContent: fullText,
                filename: previewTarget.fileName || previewTarget.filename || '학생 답안',
                fileType: 'text/plain',
              }
            : null,
          error: fullText ? '' : '제출물을 불러오지 못했어요',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [loadSubmissionPreview, previewTarget, selectedIndex, evidenceOverlayActive, evidenceAnchorsPending, ownAnswerMode]);

  if (!selectedResult) {
    return null;
  }

  const selectedName = getScoringStudentName(selectedResult, `학생 ${selectedIndex + 1}`) || `학생 ${selectedIndex + 1}`;
  const selectedNameIsPlaceholder = selectedResult.name_source === 'placeholder'
    || isPlaceholderStudentName(getScoringStudentName(selectedResult));
  const canEdit = typeof onSaveResult === 'function' && !selectedResult.error;
  const canEditScores = canEdit && !selectedGradeOnly;

  const startEdit = (key, draft) => {
    if (editing?.saving) return;
    setEditing({ key, draft, saving: false, error: '' });
  };
  const cancelEdit = () => {
    setEditing(prev => (prev?.saving ? prev : null));
  };
  const changeDraft = (patch) => {
    setEditing(prev => (prev ? { ...prev, draft: { ...prev.draft, ...patch }, error: '' } : prev));
  };
  const submitEdit = async (built) => {
    if (!editing || editing.saving) return;
    if (built.error) {
      setEditing(prev => (prev ? { ...prev, error: built.error } : prev));
      return;
    }
    const editKey = editing.key;
    const resultIndex = selectedIndex;
    setEditing(prev => (prev ? { ...prev, saving: true, error: '' } : prev));
    try {
      await onSaveResult(resultIndex, built.updates);
      setEditing(prev => (prev?.key === editKey ? null : prev));
    } catch {
      setEditing(prev => (prev?.key === editKey
        ? { ...prev, saving: false, error: '저장하지 못했어요. 다시 시도해 주세요' }
        : prev));
    }
  };
  const buildScoreEditProps = ({ questionIndex = null, itemIndex = null }) => ({
    draft: editing?.draft || { score: '', reason: '' },
    saving: Boolean(editing?.saving),
    error: editing?.error || '',
    onChange: changeDraft,
    onCancel: cancelEdit,
    onSave: () => submitEdit(buildItemScoreEdit(selectedResult, {
      questionIndex,
      itemIndex,
      score: editing?.draft?.score,
      reason: editing?.draft?.reason,
    })),
  });
  const getItemEdit = (key, item, location) => {
    if (!canEditScores) return null;
    if (editing?.key === key) {
      return { isEditing: true, editorProps: buildScoreEditProps(location) };
    }
    return {
      onStart: () => startEdit(key, { score: String(item?.score ?? ''), reason: item?.reason || '' }),
    };
  };
  const submissionLabel = typeof getSubmissionLabel === 'function'
    ? getSubmissionLabel(selectedResult, selectedIndex)
    : (selectedResult.fileName || selectedResult.filename || (selectedResult.answer_text ? '직접 입력한 답안' : '제출물'));
  const renderSubmissionPreview = () => {
    const preview = submissionPreview.data || {};
    const filename = preview.filename || submissionLabel || previewTarget.fileName || previewTarget.filename || '제출물';
    const fileType = preview.fileType || preview.file_type || previewTarget.file_type || '';
    const previewUrl = preview.previewUrl || preview.preview_url || preview.url || '';
    const previewHtml = preview.previewHtml || preview.preview_html || '';
    const textContent = preview.textContent || preview.text_content || '';
    const fallbackParts = buildSubmissionFallbackParts(selectedResult);
    const fallbackText = [fallbackParts.originalText, fallbackParts.evidenceText].filter(Boolean).join('\n\n');
    const page = sourcePageNumbers[0] || preview.pages || previewTarget.pages || selectedResult.pages || '';

    if (ownAnswerMode) {
      const hasHighlight = ownAnswerSegments.some(segment => segment.highlighted);
      return (
        <div className="scoring-review-own-answer" data-testid="scoring-review-own-answer">
          <div className="scoring-review-own-answer-notice">
            <div>
              <strong>
                {selectedNameIsPlaceholder ? '이 학생' : selectedName}
                {ownAnswerText ? ' 답안만 모아 보여드려요' : ' 답안 글을 불러오지 못했어요'}
              </strong>
              <p>
                원본 파일에는 여러 학생 답안이 함께 있어요.
                {ownAnswerText && hasHighlight ? ' 노란 부분이 채점 근거로 쓴 문장이에요.' : ''}
                {ownAnswerText ? '' : ' 원본에서 직접 찾아 주세요.'}
              </p>
            </div>
            <button
              type="button"
              className="scoring-review-own-answer-original-btn"
              onClick={() => {
                reviewUxRef.current.originalToggles += 1;
                setShowFullOriginal(true);
              }}
            >
              원본 파일 전체 보기
            </button>
          </div>
          {ownAnswerText && (
            <pre className="scoring-review-own-answer-text">
              {ownAnswerSegments.map((segment, index) => (
                segment.highlighted
                  ? <mark key={index} className="scoring-review-own-answer-mark">{segment.text}</mark>
                  : <React.Fragment key={index}>{segment.text}</React.Fragment>
              ))}
            </pre>
          )}
        </div>
      );
    }

    if (ownAnswerPreferred && showFullOriginal) {
      return (
        <div className="scoring-review-own-answer scoring-review-own-answer--original">
          <div className="scoring-review-own-answer-bar">
            <span>원본 파일 전체를 보고 있어요</span>
            <button
              type="button"
              className="scoring-review-own-answer-back-btn"
              onClick={() => setShowFullOriginal(false)}
            >
              {selectedNameIsPlaceholder ? '이 학생' : selectedName} 답안만 보기
            </button>
          </div>
          {renderSourcePreview({ filename, fileType, previewUrl, previewHtml, textContent, fallbackParts, fallbackText, page })}
        </div>
      );
    }

    return renderSourcePreview({ filename, fileType, previewUrl, previewHtml, textContent, fallbackParts, fallbackText, page });
  };

  const renderSourcePreview = ({ filename, fileType, previewUrl, previewHtml, textContent, fallbackParts, fallbackText, page }) => {
    if (evidenceOverlayActive) {
      return (
        <div className="scoring-review-inline-file scoring-review-inline-file--evidence">
          <ScoringEvidencePages
            recordId={recordId}
            resultIndex={selectedIndex}
            doc={evidenceDoc}
            located={locatedAnchors}
            activeKey={activeEvidenceKey}
            onSelect={handleSelectFromSegment}
            registerSegmentRef={registerSegmentRef}
            onUnavailable={() => setEvidenceOverlayUnavailable(true)}
          />
        </div>
      );
    }

    if (submissionPreview.loading || evidenceAnchorsPending) {
      return <div className="scoring-review-preview-loading">제출물을 불러오는 중...</div>;
    }

    if (textContent) {
      return <pre className="scoring-review-answer-preview scoring-review-answer-preview--large">{textContent}</pre>;
    }

    if (previewHtml) {
      return (
        <iframe
          srcDoc={previewHtml}
          title={filename}
          className="scoring-review-inline-pdf"
          sandbox="allow-same-origin"
        />
      );
    }

    if (previewUrl && isImagePreview(fileType, filename)) {
      return (
        <div className="scoring-review-inline-file">
          <img src={previewUrl} alt={filename} className="scoring-review-inline-image" />
        </div>
      );
    }

    if (previewUrl && isPdfPreview(fileType, filename)) {
      const frame = (
        <iframe
          src={buildPdfPreviewSrc(previewUrl, page)}
          title={filename}
          className="scoring-review-inline-pdf"
        />
      );
      if (sourcePageNumbers.length === 0 || !isSplitFromSharedFile(selectedResult, validResults)) return frame;
      return (
        <div className="scoring-review-own-answer scoring-review-own-answer--original">
          <div className="scoring-review-own-answer-bar">
            <span>{selectedNameIsPlaceholder ? '이 학생' : selectedName} 답안은 원본 {formatSourcePages(sourcePageNumbers)}에 있어요</span>
          </div>
          {frame}
        </div>
      );
    }

    if (submissionPreview.error) {
      return (
        <div className="scoring-review-fallback">
          <strong>원본 파일 미리보기를 만들지 못했어요</strong>
          <p className="scoring-review-fallback-reason">
            파일 형식이 브라우저 미리보기를 지원하지 않거나, 서버 변환 과정에서 일시적으로 실패했어요.
            대신 채점에 사용된 제출물 근거를 보여드려요.
          </p>
          {fallbackText ? (
            <>
              {fallbackParts.originalText && (
                <div className="scoring-review-fallback-section">
                  <h5>제출물 원본 텍스트</h5>
                  <pre>{fallbackParts.originalText}</pre>
                </div>
              )}
              {fallbackParts.evidenceText && (
                <div className="scoring-review-fallback-section">
                  <h5>채점에 사용된 제출물 근거</h5>
                  <pre>{fallbackParts.evidenceText}</pre>
                </div>
              )}
            </>
          ) : (
            <p>채점 결과의 항목별 근거를 함께 확인해주세요.</p>
          )}
        </div>
      );
    }

    if (fallbackText) {
      return (
        <div className="scoring-review-fallback">
          <strong>원본 미리보기 대신 추출한 내용을 보여드려요</strong>
          <p className="scoring-review-fallback-reason">
            원본 파일을 바로 표시할 수 없는 형식이거나 변환 미리보기를 만들지 못했어요.
            아래 내용은 채점에 사용된 제출물 근거예요.
          </p>
          {fallbackParts.originalText && (
            <div className="scoring-review-fallback-section">
              <h5>제출물 원본 텍스트</h5>
              <pre>{fallbackParts.originalText}</pre>
            </div>
          )}
          {fallbackParts.evidenceText && (
            <div className="scoring-review-fallback-section">
              <h5>채점에 사용된 제출물 근거</h5>
              <pre>{fallbackParts.evidenceText}</pre>
            </div>
          )}
        </div>
      );
    }

    return <div className="scoring-review-fallback"><strong>제출물 미리보기를 준비하지 못했어요</strong><p>{filename}</p></div>;
  };

  const renderRubricPanel = () => (
    <section className="scoring-review-panel scoring-review-panel--rubric">
      <div className="scoring-review-panel-head">
        <span className="scoring-review-kicker">기준</span>
        <h4>{rubric?.subject || questionSet?.title || '채점 기준'}</h4>
        <p>{totalPoints ? `총 ${totalPoints}점 기준으로 채점 판단을 대조해요` : '교사가 올린 기준과 채점 판단을 함께 확인해요'}</p>
      </div>

      {rubricItems.length > 0 && (
        <div className="scoring-review-rubric-list">
          {rubricItems.map((item, index) => (
            <div key={index} className="scoring-review-rubric-item">
              <div>
                <strong>{getRubricItemName(item)}</strong>
                {item.description && <p>{item.description}</p>}
              </div>
              <span>{getRubricItemMaxScore(item)}점</span>
            </div>
          ))}
        </div>
      )}

      {questions.length > 0 && (
        <div className="scoring-review-question-list">
          <span className="scoring-review-subtitle">문항</span>
          {questions.map((question, index) => (
            <div key={question.question_id || index} className="scoring-review-question-chip">
              <span>{getQuestionTitle(question, index)}</span>
              <strong>{getQuestionMaxScore(question)}점</strong>
            </div>
          ))}
        </div>
      )}

      {rubricText && (
        <details className="scoring-review-raw-rubric">
          <summary>기준서 원문 보기</summary>
          <pre>{rubricText}</pre>
        </details>
      )}
    </section>
  );

  const renderSubmissionPanel = () => (
    <section className="scoring-review-panel scoring-review-panel--submission" ref={submissionPanelRef}>
      <div className="scoring-review-submission-card">
        {attachments.length > 1 && (
          <div className="scoring-review-attachment-tabs">
            {attachments.map((attachment, index) => (
              <button
                key={`${attachment.storage_path || attachment.filename || index}-${index}`}
                type="button"
                className={selectedAttachmentIndex === index ? 'is-active' : ''}
                onClick={() => setSelectedAttachmentIndex(index)}
              >
                {attachment.fileName || `파일 ${index + 1}`}
              </button>
            ))}
          </div>
        )}
        {renderSubmissionPreview()}
      </div>
    </section>
  );

  const renderFeedbackPanel = () => (
    <section className="scoring-review-panel scoring-review-panel--feedback" ref={feedbackPanelRef}>
      <div className="scoring-review-score-card">
        <div className="scoring-review-name-area">
          <span className="scoring-review-kicker">채점 판단</span>
          {editing?.key === 'name' ? (
            <div className="scoring-review-name-editor">
              <input
                aria-label="학생 이름"
                autoFocus
                className="scoring-review-name-input"
                disabled={editing.saving}
                maxLength={40}
                onChange={(event) => changeDraft({ name: event.target.value })}
                onKeyDown={(event) => handleEditorKeyDown(event, {
                  onSave: () => submitEdit(buildStudentNameEdit(editing.draft.name)),
                  onCancel: cancelEdit,
                })}
                placeholder={`학생 ${selectedIndex + 1}`}
                value={editing.draft.name}
              />
              <InlineEditError message={editing.error} />
              <InlineEditActions
                saving={editing.saving}
                onSave={() => submitEdit(buildStudentNameEdit(editing.draft.name))}
                onCancel={cancelEdit}
              />
            </div>
          ) : (
            <h4>
              {canEdit ? (
                <button
                  type="button"
                  className={`scoring-review-name-btn ${selectedNameIsPlaceholder ? 'is-placeholder' : ''}`}
                  onClick={() => startEdit('name', { name: selectedNameIsPlaceholder ? '' : selectedName })}
                  aria-label={`${selectedName} 이름 ${selectedNameIsPlaceholder ? '입력하기' : '고치기'}`}
                >
                  <span>{selectedName}</span>
                  <em>{selectedNameIsPlaceholder ? '이름 입력' : '수정'}</em>
                </button>
              ) : selectedName}
            </h4>
          )}
        </div>
        <strong>{selectedScoreLabel}</strong>
      </div>

      <ScoringDistributionNotice result={selectedResult} />

      {selectedResult.question_results?.length > 0 && (
        <div className="scoring-review-block">
          <h5>문항별 판단</h5>
          {selectedResult.question_results.map((question, index) => {
            const questionTitle = question.question_title || `${question.question_id || index + 1}번 문항`;
            const questionKey = `question:${index}`;
            const hasItems = question.item_scores?.length > 0;
            const questionEditable = canEditScores && !hasItems;
            return (
              <div key={question.question_id || index} className="scoring-review-question-result">
                <div className="scoring-review-row-head">
                  <strong>{questionTitle}</strong>
                  <span className="scoring-review-row-score">
                    {selectedGradeOnly ? (question.matched_level || getGradeOnlySummary(question)) : `${question.score || 0}/${question.max_score || 0}점`}
                    {questionEditable && editing?.key !== questionKey && (
                      <button
                        type="button"
                        className="scoring-review-inline-edit-btn"
                        onClick={() => startEdit(questionKey, { score: String(question.score ?? ''), reason: question.reason || '' })}
                        aria-label={`${questionTitle} 점수 고치기`}
                      >
                        고치기
                      </button>
                    )}
                  </span>
                </div>
                {questionEditable && editing?.key === questionKey && (
                  <ScoringScoreEditor
                    label={questionTitle}
                    maxScore={question.max_score}
                    {...buildScoreEditProps({ questionIndex: index })}
                  />
                )}
                {question.answer_excerpt && <p className="scoring-review-excerpt">답안: {question.answer_excerpt}</p>}
                {hasItems && (
                  <div className="scoring-review-question-items">
                    {question.item_scores.map((item, itemIndex) => {
                      const questionRubric = questions[index]?.rubric_criteria;
                      return (
                        <ScoringEvidenceItem
                          key={itemIndex}
                          criteria={getMatchedCriteria(questionRubric, item)}
                          gradeOnly={selectedGradeOnly}
                          item={item}
                          itemIndex={itemIndex}
                          questionIndex={index}
                          evidenceLink={evidenceLink}
                          edit={getItemEdit(`question:${index}:item:${itemIndex}`, item, { questionIndex: index, itemIndex })}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {selectedResult.item_scores?.length > 0 && (
        <div className="scoring-review-block">
          <h5>항목별 근거</h5>
          {selectedResult.item_scores.map((item, index) => {
            const matchedCriteria = getMatchedCriteria(rubric, item);
            return (
              <ScoringEvidenceItem
                key={index}
                criteria={matchedCriteria}
                gradeOnly={selectedGradeOnly}
                item={item}
                itemIndex={index}
                evidenceLink={evidenceLink}
                edit={getItemEdit(`item:${index}`, item, { itemIndex: index })}
              />
            );
          })}
        </div>
      )}

      {(selectedResult.strengths?.length > 0 || selectedResult.improvements?.length > 0) && (
        <div className="scoring-review-feedback-pair">
          {selectedResult.strengths?.length > 0 && (
            <div>
              <h5>잘한 점</h5>
              <ul>{selectedResult.strengths.map((item, index) => <li key={index}>{item}</li>)}</ul>
            </div>
          )}
          {selectedResult.improvements?.length > 0 && (
            <div>
              <h5>보완할 점</h5>
              <ul>{selectedResult.improvements.map((item, index) => <li key={index}>{item}</li>)}</ul>
            </div>
          )}
        </div>
      )}

      {editing?.key === 'detailed_feedback' ? (
        <div className="scoring-review-block is-editing">
          <h5>종합 의견</h5>
          <textarea
            aria-label="종합 의견"
            autoFocus
            className="scoring-review-feedback-input"
            disabled={editing.saving}
            onChange={(event) => changeDraft({ text: event.target.value })}
            onKeyDown={(event) => handleEditorKeyDown(event, {
              onSave: () => submitEdit(buildFeedbackEdit('detailed_feedback', editing.draft.text)),
              onCancel: cancelEdit,
              saveOnEnter: false,
            })}
            rows={5}
            value={editing.draft.text}
          />
          <InlineEditError message={editing.error} />
          <InlineEditActions
            saving={editing.saving}
            onSave={() => submitEdit(buildFeedbackEdit('detailed_feedback', editing.draft.text))}
            onCancel={cancelEdit}
          />
        </div>
      ) : (selectedResult.detailed_feedback || canEdit) && (
        <div className="scoring-review-block">
          <div className="scoring-review-block-head">
            <h5>종합 의견</h5>
            {canEdit && (
              <button
                type="button"
                className="scoring-review-inline-edit-btn"
                onClick={() => startEdit('detailed_feedback', { text: selectedResult.detailed_feedback || '' })}
                aria-label="종합 의견 고치기"
              >
                {selectedResult.detailed_feedback ? '고치기' : '쓰기'}
              </button>
            )}
          </div>
          {selectedResult.detailed_feedback && <p>{selectedResult.detailed_feedback}</p>}
        </div>
      )}

      {selectedResult.teacher_comment && (
        <div className="scoring-review-block scoring-review-block--green">
          <h5>교과/세특 생기부</h5>
          <p>{selectedResult.teacher_comment}</p>
        </div>
      )}

      {actionSlot}
    </section>
  );

  const content = (
    <div className={`scoring-review-workspace ${className}`.trim()} ref={workspaceRef}>
      {/* 모달 변형은 제목·닫기 버튼을 Dialog 헤더가 그린다 */}
      {variant !== 'modal' && (
        <div className="scoring-review-heading">
          <div>
            <span className="scoring-review-kicker">한눈에 검토</span>
            <h3>{title}</h3>
            <p>기준, 제출물, 채점 판단을 같은 화면에서 비교해요.</p>
          </div>
          {onClose && (
            <button type="button" className="scoring-review-close-btn" onClick={onClose}>
              닫기
            </button>
          )}
        </div>
      )}

      {validResults.length > 1 && (
        <div className="scoring-student-rail-wrap">
          <button
            type="button"
            className="scoring-student-rail-nav"
            onClick={() => goToNeighborStudent(-1)}
            disabled={prevStudentIndex < 0}
            aria-label="이전 학생 보기"
            title="이전 학생 (← 키)"
          >
            ‹
          </button>
          <div className="scoring-student-rail" aria-label="학생 선택" ref={studentRailRef}>
            {validResults.map((result, index) => {
              const name = getScoringStudentName(result, `학생 ${index + 1}`) || `학생 ${index + 1}`;
              const namePending = result.name_source === 'placeholder' || isPlaceholderStudentName(getScoringStudentName(result));
              const isSelected = index === selectedIndex;
              return (
                <button
                  key={index}
                  type="button"
                  className={`scoring-student-card ${isSelected ? 'is-selected' : ''} ${result.error ? 'has-error' : ''}`}
                  onClick={() => !result.error && selectStudent(index, 'card')}
                  disabled={Boolean(result.error)}
                  aria-current={isSelected ? 'true' : undefined}
                  data-student-index={index}
                >
                  <span className={namePending ? 'is-placeholder-name' : ''}>{name}</span>
                  <strong>{result.error ? '오류' : (isGradeOnlyResult(result, rubric) ? getGradeOnlySummary(result) : `${getResultTotalScore(result)}점`)}</strong>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="scoring-student-rail-nav"
            onClick={() => goToNeighborStudent(1)}
            disabled={nextStudentIndex < 0}
            aria-label="다음 학생 보기"
            title="다음 학생 (→ 키)"
          >
            ›
          </button>
          {selectedPosition > 0 && (
            <span className="scoring-review-nav-count" aria-live="polite">
              <strong>{selectedPosition}</strong> / {selectableStudentIndexes.length}
            </span>
          )}
        </div>
      )}

      <div className="scoring-review-mobile-tabs" role="tablist" aria-label="검토 영역">
        {[
          ['rubric', '기준'],
          ['submission', '제출물'],
          ['feedback', '채점 판단'],
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={mobileTab === value ? 'is-active' : ''}
            onClick={() => {
              setMobileTab(value);
              if (value === 'rubric') setRubricCollapsed(false);
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div className={`scoring-review-panels ${rubricCollapsed ? 'is-rubric-collapsed' : ''}`}>
        <div className={`scoring-review-panel-slot scoring-review-rubric-column ${mobileTab === 'rubric' ? 'is-mobile-active' : ''}`}>
          <button
            type="button"
            className="scoring-review-rubric-collapse-btn"
            onClick={() => setRubricCollapsed(prev => !prev)}
            aria-label={rubricCollapsed ? '기준 패널 열기' : '기준 패널 접기'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              {rubricCollapsed ? (
                <polyline points="9 18 15 12 9 6" />
              ) : (
                <polyline points="15 18 9 12 15 6" />
              )}
            </svg>
          </button>
          {!rubricCollapsed && renderRubricPanel()}
        </div>
        <div className={`scoring-review-panel-slot ${mobileTab === 'submission' ? 'is-mobile-active' : ''}`}>{renderSubmissionPanel()}</div>
        <div className={`scoring-review-panel-slot ${mobileTab === 'feedback' ? 'is-mobile-active' : ''}`}>{renderFeedbackPanel()}</div>
      </div>
    </div>
  );

  if (variant === 'modal') {
    // 스크롤 잠금은 위의 useAppScrollLock이 결과가 있을 때만 잡는다 (Dialog 중복 잠금 방지)
    return (
      <Dialog
        closeLabel="검토 닫기"
        flush
        onClose={onClose || undefined}
        open
        panelClassName="scoring-review-modal-shell"
        scrollLock={false}
        size="lg"
        testId="scoring-review-modal"
        title={title}
      >
        {content}
      </Dialog>
    );
  }

  return content;
}

function ScoringReviewQueueSummary({ queue, results, rubric, onSelect }) {
  const [showAll, setShowAll] = useState(false);
  const { total, queued } = queue;
  if (queued.length === 0) {
    return (
      <div className="scoring-review-queue is-clear" data-testid="scoring-review-queue">
        <strong>{total}명 모두 답안에서 근거를 찾았어요</strong>
      </div>
    );
  }
  const visible = showAll ? queued : queued.slice(0, REVIEW_QUEUE_VISIBLE_LIMIT);
  const hiddenCount = queued.length - visible.length;
  return (
    <section className="scoring-review-queue" aria-label="먼저 볼 학생" data-testid="scoring-review-queue">
      <div className="scoring-review-queue-head">
        <strong>{total}명 중 {queued.length}명만 먼저 봐 주세요</strong>
        {total > queued.length && <span>나머지 {total - queued.length}명은 답안에서 근거를 찾았어요</span>}
      </div>
      <ul className="scoring-review-queue-list">
        {visible.map(({ index, reasons }) => {
          const result = results[index];
          const name = getScoringStudentName(result, `학생 ${index + 1}`) || `학생 ${index + 1}`;
          return (
            <li key={index}>
              <button type="button" className="scoring-review-queue-row" onClick={() => onSelect?.(index)}>
                <span className="scoring-review-queue-name">{name}</span>
                <span className="scoring-review-queue-reason">{reasons[0].label}</span>
                <strong>{isGradeOnlyResult(result, rubric) ? getGradeOnlySummary(result) : `${getResultTotalScore(result)}점`}</strong>
              </button>
            </li>
          );
        })}
      </ul>
      {hiddenCount > 0 && (
        <button type="button" className="scoring-review-queue-more" onClick={() => setShowAll(true)}>
          {hiddenCount}명 더 보기
        </button>
      )}
    </section>
  );
}

export function ScoringReviewLauncher({
  onSelect,
  rubric = null,
  results = [],
  answerTexts = [],
}) {
  const validResults = useMemo(() => (Array.isArray(results) ? results : []), [results]);
  const queue = useMemo(() => buildReviewQueue(validResults, { answerTexts }), [validResults, answerTexts]);
  if (validResults.length === 0) return null;
  const queuedIndexes = new Set(queue.queued.map(entry => entry.index));

  return (
    <div className={`scoring-review-launcher ${queue.total > 1 ? 'has-queue' : ''}`}>
      {queue.total > 1 && (
        <ScoringReviewQueueSummary queue={queue} results={validResults} rubric={rubric} onSelect={onSelect} />
      )}
      <div className="scoring-review-launcher-list">
        {validResults.map((result, index) => {
          const name = getScoringStudentName(result, `학생 ${index + 1}`) || `학생 ${index + 1}`;
          const isError = Boolean(result?.error);
          const isQueued = queue.total > 1 && queuedIndexes.has(index);
          return (
            <button
              key={index}
              type="button"
              className={`scoring-review-launcher-card ${isError ? 'has-error' : ''} ${isQueued ? 'is-queued' : ''}`}
              disabled={isError}
              onClick={() => onSelect?.(index)}
            >
              <div>
                <span>{name}</span>
                <strong>{isError ? '오류' : (isGradeOnlyResult(result, rubric) ? getGradeOnlySummary(result) : `${getResultTotalScore(result)}점`)}</strong>
              </div>
              {!isError && <small>{isQueued ? '먼저 보기' : '근거 보기'}</small>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default ScoringReviewWorkspace;
